import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Clock, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";

// ✅ No backend required — static services data
const categories = ["all", "Hair", "Color", "Spa", "Skin", "Bridal", "Grooming"];

const STATIC_SERVICES = [
  {
    id: 1,
    name: "Haircut & Styling",
    category: "Hair",
    description: "Precision haircut, wash & blow-dry styling.",
    price: "₹499+",
    duration: "45 min",
    image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=600",
  },
  {
    id: 2,
    name: "Hair Color",
    category: "Color",
    description: "Global color, highlights & premium finishing.",
    price: "₹1499+",
    duration: "90 min",
    image: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600",
  },
  {
    id: 3,
    name: "Cleanup / Facial",
    category: "Skin",
    description: "Glow facial & cleanup for fresh, healthy skin.",
    price: "₹799+",
    duration: "60 min",
    image: "https://images.unsplash.com/photo-1600334129128-685c5582fd35?w=600",
  },
  {
    id: 4,
    name: "Hair Spa",
    category: "Spa",
    description: "Deep conditioning spa therapy for silky hair.",
    price: "₹999+",
    duration: "60 min",
    image: "https://images.unsplash.com/photo-1527799820374-dcf8d9d4a388?w=600",
  },
  {
    id: 5,
    name: "Bridal Makeup",
    category: "Bridal",
    description: "Complete bridal look with premium products.",
    price: "₹4999+",
    duration: "120 min",
    image: "https://images.unsplash.com/photo-1522338140500-5902f0a0d1f0?w=600",
  },
  {
    id: 6,
    name: "Grooming Package",
    category: "Grooming",
    description: "Hair + beard + styling package.",
    price: "₹999+",
    duration: "60 min",
    image: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600",
  },
];

export default function Services() {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // ✅ Load static services once (no API calls)
  useEffect(() => {
    setServices(STATIC_SERVICES);
    setFilteredServices(STATIC_SERVICES);
    setLoading(false);
  }, []);

  // ✅ Filter logic (safe even if services changes)
  useEffect(() => {
    let filtered = Array.isArray(services) ? services : [];

    if (selectedCategory !== "all") {
      filtered = filtered.filter((s) => s.category === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          (s.name || "").toLowerCase().includes(query) ||
          (s.description || "").toLowerCase().includes(query)
      );
    }

    setFilteredServices(filtered);
  }, [selectedCategory, searchQuery, services]);

  return (
    <div className="pt-24" data-testid="services-page">
      {/* Header */}
      <section className="section-padding py-16 bg-obsidian text-white text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">
            What We Offer
          </p>
          <h1 className="font-heading text-5xl md:text-6xl tracking-tight mb-4">
            Our Services
          </h1>
          <p className="text-white/70 max-w-xl mx-auto">
            Discover our comprehensive range of luxury beauty services
          </p>
        </motion.div>
      </section>

      {/* Filters */}
      <section className="section-padding py-8 bg-white border-b sticky top-[72px] z-30">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm transition-all ${
                  selectedCategory === category
                    ? "bg-obsidian text-white"
                    : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                }`}
                data-testid={`category-${category}`}
              >
                {category === "all" ? "All Services" : category}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-64">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
              size={18}
            />
            <Input
              placeholder="Search services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-full border-neutral-200"
              data-testid="search-input"
            />
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding section-gap">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-96 w-full" />
            ))}
          </div>
        ) : (Array.isArray(filteredServices) ? filteredServices : []).length ===
          0 ? (
          <div className="text-center py-20">
            <p className="text-neutral-500">
              No services found matching your criteria.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {(Array.isArray(filteredServices) ? filteredServices : []).map(
              (service, index) => (
                <motion.div
                  key={service.id ?? index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="group"
                  data-testid={`service-item-${index}`}
                >
                  <div className="relative overflow-hidden aspect-[4/5] mb-6">
                    <img
                      src={
                        service.image ||
                        "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=600"
                      }
                      alt={service.name || "Service"}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Link
                        to="/book"
                        className="btn-gold text-xs py-2 w-full text-center block"
                      >
                        Book Now
                      </Link>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-widest text-gold mb-2">
                      {service.category || "Service"}
                    </p>
                    <h3 className="font-heading text-2xl mb-2">
                      {service.name || "Service"}
                    </h3>
                    <p className="text-neutral-500 text-sm mb-4 line-clamp-2">
                      {service.description || ""}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-medium text-obsidian">
                        {service.price || ""}
                      </span>
                      {service.duration && (
                        <span className="flex items-center gap-1 text-sm text-neutral-400">
                          <Clock size={14} />
                          {service.duration}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              )
            )}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="section-padding py-20 bg-neutral-50 text-center">
        <h2 className="font-heading text-3xl md:text-4xl mb-4">
          Can't find what you're looking for?
        </h2>
        <p className="text-neutral-500 mb-8">
          Contact us for custom services and packages
        </p>
        {/* ✅ Avoid /contact unless you have that page */}
        <Link to="/book" className="btn-primary inline-flex items-center gap-2">
          Book / Contact Us <ArrowRight size={16} />
        </Link>
      </section>
    </div>
  );
}
